import { useCallback, useEffect, useState } from "react";
import Header from "./components/Header";
import Chart from "./components/Chart";
import StrategyEditor from "./components/StrategyEditor";
import BacktestPanel from "./components/BacktestPanel";
import BrokerSetup from "./components/BrokerSetup";
import { useMarketData } from "./hooks/useMarketData";
import { ClientSideBacktester } from "./backtester/ClientSideBacktester";
import { compileStrategy, DEFAULT_STRATEGY_CODE } from "./lib/strategy";
import {
  dispatchSignal,
  getWebhookConfig,
  setWebhookConfig,
  type SignalRequest,
  type WebhookConfig,
} from "./lib/dispatch";
import type { BacktestConfig, BacktestResult, StrategyFn } from "./types";

const DEFAULT_CONFIG: BacktestConfig = {
  initialBalance: 10000,
  lots: 0.1,
  slDistance: 4.0,
  tpDistance: 8.0,
  spread: 0.25,
  slippage: 0.05,
  commissionPerLot: 0,
  warmupBars: 50,
};

export default function App() {
  const { candles, live, toggleLive, timeframe, setTimeframe } = useMarketData();
  const [code, setCode] = useState(DEFAULT_STRATEGY_CODE);
  const [config, setConfig] = useState<BacktestConfig>(DEFAULT_CONFIG);
  const [result, setResult] = useState<BacktestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editorTab, setEditorTab] = useState<"strategy" | "backtest" | "broker">("strategy");

  // ── Strategy compilation (Stage 1 Strategy IDE) ──
  const [compiled, setCompiled] = useState<StrategyFn | null>(null);
  const [compileError, setCompileError] = useState<string | null>(null);

  useEffect(() => {
    try {
      setCompiled(compileStrategy(code));
      setCompileError(null);
    } catch (e) {
      setCompiled(null);
      setCompileError(e instanceof Error ? e.message : "Strategy compile error");
    }
  }, [code]);

  // ── Live signal detection → dispatch to Stage 2 ──
  const [liveSignal, setLiveSignal] = useState<SignalRequest | null>(null);
  const [dispatching, setDispatching] = useState(false);
  const [dispatchMsg, setDispatchMsg] = useState<{
    tone: "ok" | "warn" | "err";
    text: string;
  } | null>(null);
  const [showWebhook, setShowWebhook] = useState(false);
  const [webhook, setWebhook] = useState<WebhookConfig>(() => getWebhookConfig());

  useEffect(() => {
    if (!live || !compiled || candles.length <= config.warmupBars) {
      setLiveSignal(null);
      return;
    }
    try {
      const idx = candles.length - 1;
      const sig = compiled(candles[idx], idx, candles);
      setLiveSignal(
        sig
          ? {
              symbol: "XAUUSD",
              timeframe,
              signal_type: sig,
              price: candles[idx].close,
              setup_name: "live",
              strategy_id: "stage1-strategy-ide",
            }
          : null
      );
    } catch {
      setLiveSignal(null);
    }
  }, [candles, live, compiled, config.warmupBars, timeframe]);

  const handleDispatch = useCallback(async () => {
    if (!liveSignal || dispatching) return;
    setDispatching(true);
    setDispatchMsg(null);
    const res = await dispatchSignal(liveSignal);
    setDispatching(false);

    const s = String(res.body.status ?? "NO_RESPONSE");
    if (res.status === 401 || res.status === 429) {
      setDispatchMsg({ tone: "err", text: `${s} — check token / rate limit (${res.status})` });
    } else if (s === "APPROVED_AND_DISPATCHED" || s === "APPROVED") {
      const oracle = res.body.oracle ? ` · ${JSON.stringify(res.body.oracle)}` : "";
      setDispatchMsg({ tone: "ok", text: `✓ ${s}${oracle}` });
    } else if (s === "REJECTED") {
      setDispatchMsg({ tone: "warn", text: `⛔ Jev AI rejected: ${String(res.body.reason ?? "no reason")}` });
    } else {
      setDispatchMsg({ tone: "err", text: `${s} — worker reachable? (${res.status})` });
    }
  }, [liveSignal, dispatching]);

  const runBacktest = useCallback(() => {
    try {
      setError(null);
      const strategyFn = compileStrategy(code);
      const engine = new ClientSideBacktester(config);
      const res = engine.runBacktest(candles, strategyFn);

      if (res.metrics.totalTrades === 0) {
        setError("No trades generated — the strategy produced zero signals on this dataset.");
      }
      setResult(res);
      setEditorTab("backtest");
    } catch (e) {
      setResult(null);
      setError(e instanceof Error ? `Strategy error: ${e.message}` : "Unknown strategy error");
    }
  }, [code, config, candles]);

  const patchConfig = useCallback((patch: Partial<BacktestConfig>) => {
    setConfig((prev) => ({ ...prev, ...patch }));
  }, []);

  return (
    <div className="flex h-full flex-col bg-tv-bg text-tv-text">
      <Header
        candles={candles}
        live={live}
        onToggleLive={toggleLive}
        timeframe={timeframe}
        onTimeframeChange={setTimeframe}
      />

      <main className="flex min-h-0 flex-1">
        {/* ── Chart ── */}
        <section className="relative min-w-0 flex-1">
          <Chart candles={candles} result={result} />
          {result && (
            <div className="pointer-events-none absolute left-3 top-3 rounded border border-tv-border bg-tv-panel/90 px-3 py-1.5 font-mono text-[11px] backdrop-blur">
              <span className="text-tv-muted">Backtest:</span>{" "}
              <span
                className={
                  result.metrics.netProfit >= 0 ? "text-tv-green" : "text-tv-red"
                }
              >
                {result.metrics.netProfit >= 0 ? "+" : ""}$
                {result.metrics.netProfit.toFixed(2)}
              </span>{" "}
              <span className="text-tv-muted">
                · {result.metrics.totalTrades} trades · WR{" "}
                {result.metrics.winRate.toFixed(1)}%
              </span>
            </div>
          )}
        </section>

        {/* ── Sidebar: Strategy IDE + Results ── */}
        <aside className="flex w-[440px] shrink-0 flex-col border-l border-tv-border bg-tv-bg xl:w-[500px]">
          {/* Tabs */}
          <div className="flex h-10 shrink-0 items-center border-b border-tv-border bg-tv-panel">
            <button
              onClick={() => setEditorTab("strategy")}
              className={`h-full border-b-2 px-4 text-xs font-semibold uppercase tracking-wider transition ${
                editorTab === "strategy"
                  ? "border-tv-blue text-tv-text"
                  : "border-transparent text-tv-muted hover:text-tv-text"
              }`}
            >
              Strategy IDE
            </button>
            <button
              onClick={() => setEditorTab("backtest")}
              className={`h-full border-b-2 px-4 text-xs font-semibold uppercase tracking-wider transition ${
                editorTab === "backtest"
                  ? "border-tv-blue text-tv-text"
                  : "border-transparent text-tv-muted hover:text-tv-text"
              }`}
            >
              Backtest Results
            </button>
            <button
              onClick={() => setEditorTab("broker")}
              className={`h-full border-b-2 px-4 text-xs font-semibold uppercase tracking-wider transition ${
                editorTab === "broker"
                  ? "border-tv-blue text-tv-text"
                  : "border-transparent text-tv-muted hover:text-tv-text"
              }`}
            >
              Broker Setup
            </button>
          </div>

          {/* Editor */}
          <div className={`min-h-0 flex-1 ${editorTab === "strategy" ? "block" : "hidden"}`}>
            <StrategyEditor value={code} onChange={setCode} />
          </div>

          {/* Results */}
          <div className={`min-h-0 flex-1 ${editorTab === "backtest" ? "block" : "hidden"}`}>
            <BacktestPanel
              config={config}
              onConfigChange={patchConfig}
              onRun={runBacktest}
              result={result}
              error={error}
            />
          </div>

          {/* Broker config (dashboard → MT5) */}
          <div className={`min-h-0 flex-1 ${editorTab === "broker" ? "block" : "hidden"}`}>
            <BrokerSetup />
          </div>

          {/* Webhook settings row */}
          {showWebhook && (
            <div className="shrink-0 border-t border-tv-border bg-tv-panel px-3 py-2">
              <div className="flex items-center gap-2">
                <input
                  value={webhook.url}
                  onChange={(e) => {
                    const url = e.target.value;
                    setWebhook((prev) => {
                      const next = { ...prev, url };
                      setWebhookConfig(next);
                      return next;
                    });
                  }}
                  placeholder="Worker URL (https://…/signal)"
                  spellCheck={false}
                  className="min-w-0 flex-1 rounded border border-tv-border bg-tv-bg px-2 py-1 font-mono text-[11px] text-tv-text outline-none focus:border-tv-blue"
                />
                <input
                  value={webhook.token}
                  onChange={(e) => {
                    const token = e.target.value;
                    setWebhook((prev) => {
                      const next = { ...prev, token };
                      setWebhookConfig(next);
                      return next;
                    });
                  }}
                  placeholder="X-Strategy-Token"
                  spellCheck={false}
                  className="w-40 rounded border border-tv-border bg-tv-bg px-2 py-1 font-mono text-[11px] text-tv-text outline-none focus:border-tv-blue"
                />
                <input
                  value={webhook.adminToken}
                  onChange={(e) => {
                    const adminToken = e.target.value;
                    setWebhook((prev) => {
                      const next = { ...prev, adminToken };
                      setWebhookConfig(next);
                      return next;
                    });
                  }}
                  placeholder="Admin Token"
                  spellCheck={false}
                  className="w-40 rounded border border-tv-border bg-tv-bg px-2 py-1 font-mono text-[11px] text-tv-text outline-none focus:border-tv-blue"
                />
              </div>
            </div>
          )}

          {/* Bottom toolbar — Run, Dispatch to Stage 2 JEV AI */}
          <div className="flex h-11 shrink-0 items-center gap-2 border-t border-tv-border bg-tv-panel px-3">
            <button
              onClick={runBacktest}
              className="rounded bg-tv-blue px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-blue-600 active:scale-[0.98]"
            >
              ▶ Run
            </button>
            <button
              onClick={handleDispatch}
              disabled={!liveSignal || dispatching}
              title={
                !liveSignal
                  ? "Waiting for a live strategy signal…"
                  : "Send current live signal to the JEV AI Worker"
              }
              className={`rounded px-4 py-1.5 text-xs font-semibold transition ${
                liveSignal && !dispatching
                  ? "bg-tv-yellow text-tv-bg hover:brightness-110 active:scale-[0.98]"
                  : "cursor-not-allowed border border-tv-border text-tv-muted"
              }`}
            >
              {dispatching ? "Dispatching…" : "↗ Dispatch to JEV AI"}
            </button>
            {liveSignal && (
              <span
                className={`rounded border border-tv-border px-2 py-1 font-mono text-[10px] font-bold ${
                  liveSignal.signal_type === "BUY" ? "text-tv-green" : "text-tv-red"
                }`}
              >
                ◉ {liveSignal.signal_type} @ {liveSignal.price.toFixed(2)}
              </span>
            )}
            <button
              onClick={() => setShowWebhook((v) => !v)}
              title="Webhook settings"
              className={`rounded border px-2 py-1.5 text-xs transition ${
                showWebhook
                  ? "border-tv-blue text-tv-blue"
                  : "border-tv-border text-tv-muted hover:text-tv-text"
              }`}
            >
              ⚙
            </button>
            {(dispatchMsg || compileError) && (
              <span
                className={`max-w-[280px] truncate font-mono text-[10px] ${
                  dispatchMsg?.tone === "ok"
                    ? "text-tv-green"
                    : dispatchMsg?.tone === "warn"
                      ? "text-tv-yellow"
                      : compileError
                        ? "text-tv-red"
                        : "text-tv-red"
                }`}
                title={dispatchMsg?.text ?? compileError ?? ""}
              >
                {dispatchMsg?.text ?? compileError}
              </span>
            )}
            <span className="ml-auto shrink-0 font-mono text-[10px] text-tv-muted">
              {candles.length} bars · {timeframe}
            </span>
          </div>
        </aside>
      </main>
    </div>
  );
}
